/**
 * HeroPointServiceModule — A6e (2026-06-03).
 *
 * Wraps the briefing-room backend's hero-points REST API. Replaces the A6
 * local-state shim by giving useHeroPointSpend a real persistence layer.
 *
 * Routes (per briefing_room_backend/routes/hero_points.py):
 *   POST   /api/modules/briefing-room/campaigns/{cid}/hero-points/initialize   → HeroPointState (idempotent)
 *   POST   /api/modules/briefing-room/campaigns/{cid}/hero-points/spend        → MutationResponse | 404 | 409
 *   POST   /api/modules/briefing-room/campaigns/{cid}/hero-points/award        → MutationResponse | 404
 *
 * (The plain `GET /hero-points` route still exists on the backend, but the
 * client `.get()` wrapper was removed 2026-06-08 after Finding #1 of the
 * VG35014 MCP playthrough: useHeroPointSpend now posts /initialize directly
 * on mount — the idempotent route returns the existing state if any, so the
 * old GET-then-404-then-POST flow was unnecessary and produced a 404 in
 * DevTools console on every fresh campaign load.)
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders() (mirrors
 * PersistenceServiceModule pattern at services/host/PersistenceServiceModule.ts).
 */
import { getAuthHeaders } from './authHeaders';

export interface LedgerEntry {
  id: string;
  delta: number;
  reason: string;
  occurred_at: string;
}

export interface HeroPointState {
  balance: number;
  initial: number;
  ledger: LedgerEntry[];
}

export interface MutationResponse {
  balance: number;
  entry: LedgerEntry;
}

export class HeroPointInsufficientBalanceError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HeroPointInsufficientBalanceError';
  }
}

const BASE_PATH = '/api/modules/briefing-room/campaigns';

export class HeroPointServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  private url(campaignId: string, suffix = ''): string {
    return `${BASE_PATH}/${encodeURIComponent(campaignId)}/hero-points${suffix}`;
  }

  /**
   * Idempotent initialize. Returns existing state if already initialized.
   */
  async initialize(campaignId: string, initial: number): Promise<HeroPointState> {
    const resp = await this.fetchFn(this.url(campaignId, '/initialize'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ initial }),
    });
    if (!resp.ok) {
      throw new Error(`HeroPointServiceModule: initialize HTTP ${resp.status}`);
    }
    return (await resp.json()) as HeroPointState;
  }

  /**
   * Spend hero points. Throws HeroPointInsufficientBalanceError on 409
   * (would go negative); throws generic Error on other non-2xx.
   */
  async spend(
    campaignId: string,
    amount: number,
    reason: string,
  ): Promise<MutationResponse> {
    const resp = await this.fetchFn(this.url(campaignId, '/spend'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ amount, reason }),
    });
    if (resp.status === 409) {
      const body = await resp.json().catch(() => ({ detail: 'insufficient' }));
      throw new HeroPointInsufficientBalanceError(
        typeof body.detail === 'string' ? body.detail : 'insufficient balance',
      );
    }
    if (!resp.ok) {
      throw new Error(`HeroPointServiceModule: spend HTTP ${resp.status}`);
    }
    return (await resp.json()) as MutationResponse;
  }

  /**
   * Award hero points (e.g., mission completion bonus).
   */
  async award(
    campaignId: string,
    amount: number,
    reason: string,
  ): Promise<MutationResponse> {
    const resp = await this.fetchFn(this.url(campaignId, '/award'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ amount, reason }),
    });
    if (!resp.ok) {
      throw new Error(`HeroPointServiceModule: award HTTP ${resp.status}`);
    }
    return (await resp.json()) as MutationResponse;
  }
}
